import type { Profile, Organization, Invitation } from '@knownow/domain'
import {
  CompleteProfileUseCase,
  GetProfileUseCase,
  CreateOrganizationUseCase,
  ListUserOrganizationsUseCase,
  GetOrganizationBySlugUseCase,
  GetInvitationByTokenUseCase,
  AcceptInvitationUseCase,
  SupabaseProfileRepository,
  SupabaseOrganizationRepository,
  SupabaseInvitationRepository,
} from '@knownow/organization'
import { supabase } from './supabase'
import { useAuthStore } from '@/stores/auth-store'

// ─── Repositories (singleton, shared Supabase client) ───────────────────────

const profileRepo = new SupabaseProfileRepository(supabase)
const orgRepo = new SupabaseOrganizationRepository(supabase)
const invitationRepo = new SupabaseInvitationRepository(supabase)

// ─── Helpers ────────────────────────────────────────────────────────────────

function requireUserId(): string {
  const userId = useAuthStore.getState().user?.id
  if (!userId) throw new Error('Not authenticated')
  return userId
}

// ─── Profile ────────────────────────────────────────────────────────────────

export async function getProfile(): Promise<Profile | null> {
  const userId = requireUserId()
  const uc = new GetProfileUseCase(profileRepo)
  return uc.execute(userId)
}

export async function completeProfile(displayName: string, avatarUrl?: string): Promise<Profile> {
  const userId = requireUserId()
  const uc = new CompleteProfileUseCase(profileRepo)
  return uc.execute({ userId, displayName, avatarUrl })
}

// ─── Organizations ──────────────────────────────────────────────────────────

export async function listOrganizations(): Promise<readonly Organization[]> {
  const userId = requireUserId()
  const uc = new ListUserOrganizationsUseCase(orgRepo)
  return uc.execute(userId)
}

export async function createOrganization(name: string, slug: string): Promise<Organization> {
  const userId = requireUserId()
  const uc = new CreateOrganizationUseCase(orgRepo)
  return uc.execute({ name, slug, userId })
}

export async function getOrganizationBySlug(slug: string): Promise<Organization> {
  const userId = requireUserId()
  const uc = new GetOrganizationBySlugUseCase(orgRepo)
  return uc.execute(slug, userId)
}

// ─── Invitations ────────────────────────────────────────────────────────────

export async function getInvitationByToken(token: string): Promise<Invitation> {
  const uc = new GetInvitationByTokenUseCase(invitationRepo)
  return uc.execute(token)
}

export async function acceptInvitation(token: string): Promise<{ organizationId: string }> {
  const userId = requireUserId()
  const uc = new AcceptInvitationUseCase(invitationRepo, orgRepo)
  return uc.execute({ token, userId })
}
